// web shim - react-native-track-player's web implementation requires
// shaka-player, but web playback goes through react-player instead
// (PlayerService is also null-stubbed on web). Provide inert hooks,
// enums, and player methods so any RNTP import resolves without
// pulling shaka into the bundle.

const noop = () => {};
const asyncNoop = () => Promise.resolve();

// Enum values mirror react-native-track-player 4.1.2.
export const State = {
  None: 'none',
  Ready: 'ready',
  Playing: 'playing',
  Paused: 'paused',
  Stopped: 'stopped',
  Loading: 'loading',
  Connecting: 'loading',
  Buffering: 'buffering',
  Error: 'error',
  Ended: 'ended',
};

export const Event = {
  PlayerError: 'player-error',
  PlaybackState: 'playback-state',
  PlaybackError: 'playback-error',
  PlaybackQueueEnded: 'playback-queue-ended',
  PlaybackTrackChanged: 'playback-track-changed',
  PlaybackActiveTrackChanged: 'playback-active-track-changed',
  PlaybackMetadataReceived: 'playback-metadata-received',
  PlaybackPlayWhenReadyChanged: 'playback-play-when-ready-changed',
  PlaybackProgressUpdated: 'playback-progress-updated',
  RemotePlay: 'remote-play',
  RemotePause: 'remote-pause',
  RemoteStop: 'remote-stop',
  RemoteNext: 'remote-next',
  RemotePrevious: 'remote-previous',
  RemoteJumpForward: 'remote-jump-forward',
  RemoteJumpBackward: 'remote-jump-backward',
  RemoteSeek: 'remote-seek',
  RemoteSetRating: 'remote-set-rating',
  RemoteDuck: 'remote-duck',
  RemoteLike: 'remote-like',
  RemoteDislike: 'remote-dislike',
  RemoteBookmark: 'remote-bookmark',
  RemotePlayId: 'remote-play-id',
  RemotePlaySearch: 'remote-play-search',
  RemoteSkip: 'remote-skip',
  MetadataChapterReceived: 'metadata-chapter-received',
  MetadataTimedReceived: 'metadata-timed-received',
  MetadataCommonReceived: 'metadata-common-received',
};

export const AppKilledPlaybackBehavior = {
  ContinuePlayback: 'continue-playback',
  PausePlayback: 'pause-playback',
  StopPlaybackAndRemoveNotification: 'stop-playback-and-remove-notification',
};

export const Capability = {
  Play: 'play',
  PlayFromId: 'play-from-id',
  PlayFromSearch: 'play-from-search',
  Pause: 'pause',
  Stop: 'stop',
  SeekTo: 'seek-to',
  Skip: 'skip',
  SkipToNext: 'skip-to-next',
  SkipToPrevious: 'skip-to-previous',
  JumpForward: 'jump-forward',
  JumpBackward: 'jump-backward',
  SetRating: 'set-rating',
  Like: 'like',
  Dislike: 'dislike',
  Bookmark: 'bookmark',
};

export const RepeatMode = { Off: 0, Track: 1, Queue: 2 };

// Stable identities so hook results are safe in effect dependency arrays.
const IDLE_PLAYBACK_STATE = { state: State.None };
const ZERO_PROGRESS = { position: 0, duration: 0, buffered: 0 };

export const usePlaybackState = () => IDLE_PLAYBACK_STATE;
export const useProgress = () => ZERO_PROGRESS;
export const useActiveTrack = () => undefined;
export const useTrackPlayerEvents = noop;

const subscription = () => ({ remove: noop });

const TrackPlayer = {
  setupPlayer: asyncNoop,
  registerPlaybackService: noop,
  addEventListener: subscription,
  add: asyncNoop,
  remove: asyncNoop,
  removeUpcomingTracks: asyncNoop,
  reset: asyncNoop,
  play: asyncNoop,
  pause: asyncNoop,
  stop: asyncNoop,
  seekTo: asyncNoop,
  seekBy: asyncNoop,
  setRate: asyncNoop,
  setVolume: asyncNoop,
  getVolume: () => Promise.resolve(1),
  skip: asyncNoop,
  skipToNext: asyncNoop,
  skipToPrevious: asyncNoop,
  updateOptions: asyncNoop,
  updateNowPlayingMetadata: asyncNoop,
  updateMetadataForTrack: asyncNoop,
  getTrack: () => Promise.resolve(undefined),
  getQueue: () => Promise.resolve([]),
  getActiveTrack: () => Promise.resolve(undefined),
  getActiveTrackIndex: () => Promise.resolve(undefined),
  getProgress: () => Promise.resolve(ZERO_PROGRESS),
  getPlaybackState: () => Promise.resolve(IDLE_PLAYBACK_STATE),
  getRate: () => Promise.resolve(1),
  getRepeatMode: () => Promise.resolve(RepeatMode.Off),
  setRepeatMode: asyncNoop,
};

export default TrackPlayer;
